import { ValidOption } from './types';
import utilSvc from '../../utilSvc';
/**
 * Date Range Modes Configuration
 *
 * This file defines all available date range calculation modes.
 * To add a new mode:
 * 1. Add the mode string to the DateRangeMode union type
 * 2. Add a calculation handler in calculateDateRange() function
 * 3. Add the mode to the appropriate modesRegistry section
 * 4. Define the UI label in the specific feature's buildDateRangeList()
 *
 * Architecture:
 * - Config is separate from calculation logic for easy extension
 * - Modes are organized by category (preset, custom, etc.)
 * - Each mode has metadata about its behavior
 */

/**
 * All available date range modes
 * These are the valid values that can be passed to calculateDateRange()
 */
export type DateRangeMode =
    | 'NONE'
    | 'TODAY'
    | 'YESTERDAY'
    | 'TODAYYESTERDAY'
    | '2DAYS'
    | '3DAYS'
    | '7DAYS'
    | '2WEEKS'
    | 'MONTHLY'
    | 'MTD'
    | 'QTR'
    | 'QTD'
    | 'QTDLAST'
    | 'CUSTOM92'
    | 'CUSTOM3';

/**
 * Const array of all DateRangeMode values, for runtime use
 */
export const DATE_RANGE_MODES: DateRangeMode[] = [
    'NONE',
    'TODAY',
    'YESTERDAY',
    'TODAYYESTERDAY',
    '2DAYS',
    '3DAYS',
    '7DAYS',
    '2WEEKS',
    'MONTHLY',
    'MTD',
    'QTR',
    'QTD',
    'QTDLAST',
    'CUSTOM92',
    'CUSTOM3',
];

/**
 * Metadata about a date range mode
 */
export interface DateRangeModeMetadata {
    /** The mode identifier */
    mode: DateRangeMode;
    /** Human-readable label */
    label: string;
    /** Whether this mode allows manual date editing */
    allowsManualEdit: boolean;
    /** Max days allowed if allowsManualEdit is true */
    maxDays?: number;
    /** Category for UI grouping */
    category: 'preset' | 'custom' | 'special';
    /**
     * Function to calculate start/end dates for this mode
     * For MONTHLY: pass { monthlyMonth, monthlyYear }
     * For QTR: pass { qtr, qtrYear }
     * For others: can ignore params
     */
    getRange: (params?: {
        today?: Date;
        monthlyMonth?: number;
        monthlyYear?: number;
        qtr?: number;
        qtrYear?: number;
    }) => { start: Date; end: Date };
}

type DateRange = { start: Date; end: Date };

const fallBackMode: DateRangeModeMetadata = {
    mode: 'NONE',
    label: '',
    allowsManualEdit: false,
    category: 'special',
    getRange: (): { start: Date; end: Date } => {
        // Same as CUSTOM3
        const d = utilSvc.dt.zeroTime(new Date());
        return { start: d, end: d };
    },
};

/**
 * Registry of all date range modes organized by category
 * This acts as the source of truth for which modes are available
 */
export const dateRangeModesRegistry: DateRangeModeMetadata[] = [
    // Preset modes - auto-calculated, no manual editing
    {
        mode: 'TODAY',
        label: 'Today',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const d = utilSvc.dt.zeroTime(new Date());
            return { start: d, end: d };
        },
    },
    {
        mode: 'YESTERDAY',
        label: 'Yesterday',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const d = utilSvc.dt.addDays(utilSvc.dt.zeroTime(new Date()), -1);
            return { start: d, end: d };
        },
    },
    {
        mode: 'TODAYYESTERDAY',
        label: 'Today & Yesterday',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const end = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.zeroTime(utilSvc.dt.addDays(end, -1));
            return { start, end };
        },
    },
    {
        mode: '2DAYS',
        label: 'Last 2 Days including Today',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const end = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.zeroTime(utilSvc.dt.addDays(end, -2));
            return { start, end };
        },
    },
    {
        mode: '3DAYS',
        label: 'Last 3 Days including Today',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const end = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.zeroTime(utilSvc.dt.addDays(end, -3));
            return { start, end };
        },
    },
    {
        mode: '7DAYS',
        label: 'Last 7 Days including Today',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const end = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.zeroTime(utilSvc.dt.addDays(end, -7));
            return { start, end };
        },
    },
    {
        mode: '2WEEKS',
        label: 'Last 2 Weeks including Today',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): { start: Date; end: Date } => {
            const end = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.zeroTime(utilSvc.dt.addDays(end, -14));
            return { start, end };
        },
    },
    {
        mode: 'MONTHLY',
        label: 'Monthly',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (params = {}): DateRange => {
            const { monthlyMonth, monthlyYear } = params;
            let baseDate: Date;
            if (typeof monthlyMonth === 'number' && typeof monthlyYear === 'number') {
                // JS months are 0-based
                baseDate = new Date(monthlyYear, monthlyMonth - 1, 1);
            } else {
                baseDate = utilSvc.dt.zeroTime(new Date());
            }
            const start = utilSvc.dt.monthStart(baseDate);
            const end = utilSvc.dt.monthEnd(baseDate);
            return { start, end };
        },
    },
    {
        mode: 'MTD',
        label: 'Month To Date',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const todayLocal = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.monthStart(todayLocal);
            const end = todayLocal;
            return { start, end };
        },
    },
    {
        mode: 'QTR',
        label: 'Quarterly',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (params = {}): DateRange => {
            const { qtr, qtrYear } = params;
            let baseDate: Date;
            if (typeof qtr === 'number' && typeof qtrYear === 'number' && qtr >= 1 && qtr <= 4) {
                // Q1: Jan, Q2: Apr, Q3: Jul, Q4: Oct
                const month = (qtr - 1) * 3;
                baseDate = new Date(qtrYear, month, 1);
            } else {
                baseDate = utilSvc.dt.zeroTime(new Date());
            }
            const start = utilSvc.dt.quarterStart(baseDate);
            // End is last day of the quarter
            const end = new Date(start.getFullYear(), start.getMonth() + 3, 0, 0, 0, 0);
            return { start, end };
        },
    },
    {
        mode: 'QTD',
        label: 'Quarter To Date',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const todayLocal = utilSvc.dt.zeroTime(new Date());
            const start = utilSvc.dt.quarterStart(todayLocal);
            const end = todayLocal;
            return { start: start ?? end, end };
        },
    },
    {
        mode: 'QTDLAST',
        label: 'Last Quarter',
        allowsManualEdit: false,
        category: 'preset',
        getRange: (): DateRange => {
            const todayLocal = utilSvc.dt.zeroTime(new Date());
            const thisQuarterStart = utilSvc.dt.quarterStart(todayLocal);
            const lastQuarterEnd = utilSvc.dt.addDays(thisQuarterStart, -1);
            const start = utilSvc.dt.quarterStart(lastQuarterEnd);
            const end = lastQuarterEnd;
            return { start, end };
        },
    },
    // Custom modes - allow manual date editing
    {
        mode: 'CUSTOM92',
        label: 'Custom Date Range (92 day max)',
        allowsManualEdit: true,
        maxDays: 92,
        category: 'custom',
        getRange: (): DateRange => {
            const d = utilSvc.dt.zeroTime(new Date());
            return { start: d, end: d };
        },
    },
    {
        mode: 'CUSTOM3',
        label: 'Custom (3 Day Max)',
        allowsManualEdit: true,
        maxDays: 3,
        category: 'custom',
        getRange: (): { start: Date; end: Date } => {
            const d = utilSvc.dt.zeroTime(new Date());
            return { start: d, end: d };
        },
    },
    // Special modes
    fallBackMode,
];

/**
 * Helper function to get metadata for a specific mode
 */
export function getModeMeta(mode: DateRangeMode): DateRangeModeMetadata {
    return dateRangeModesRegistry.find(m => m.mode === mode) ?? fallBackMode;
}

/**
 * Helper function to check if a mode allows manual date editing
 */
export function modeAllowsManualEdit(mode: DateRangeMode): boolean {
    const meta = getModeMeta(mode);
    return meta?.allowsManualEdit ?? false;
}

/**
 * Helper function to get all preset modes
 */
export function getPresetModes(): DateRangeModeMetadata[] {
    return dateRangeModesRegistry.filter(m => m.category === 'preset');
}

/**
 * Helper function to get all custom modes
 */
export function getCustomModes(): DateRangeModeMetadata[] {
    return dateRangeModesRegistry.filter(m => m.category === 'custom');
}

/**
 * Helper function to get all modes for a specific feature
 * Useful when different features need different subsets of modes
 *
 * @param featureModes - Array of mode strings specific to this feature
 * @returns Array of metadata for those modes, maintaining order
 */
export function getModesForFeature(featureModes: DateRangeMode[]): DateRangeModeMetadata[] {
    return featureModes
        .map(mode => getModeMeta(mode))
        .filter((meta): meta is DateRangeModeMetadata => meta !== undefined);
}

export function buildDateRangeListOptions(): Array<ValidOption> {
    const modes = getModesForFeature(DATE_RANGE_MODES);
    return modes
        .map(mode => ({
            lbl: mode.label,
            val: mode.mode,
        }))
        .filter(item => item.lbl !== ''); // Filter out NONE (empty label)
}

/**
 * Clamp a date range to a maximum number of days.
 * If the range exceeds maxDays, adjust the end date (if start is changed) or start date (if end is changed).
 * Always returns start <= end.
 */
export function clampDateRange(start: Date, end: Date, maxDays: number, changed: 'start' | 'end' = 'end'): DateRange {
    let s = utilSvc.dt.zeroTime(start);
    let e = utilSvc.dt.zeroTime(end);
    if (s > e) {
        // If start is after end, clamp to same day
        if (changed === 'start') {
            s = e;
        } else {
            e = s;
        }
    }
    const diffDays = Math.floor((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > maxDays - 1) {
        if (changed === 'start') {
            // Move end to be maxDays after start
            e = utilSvc.dt.addDays(s, maxDays - 1);
        } else {
            // Move start to be maxDays before end
            s = utilSvc.dt.addDays(e, -(maxDays - 1));
        }
    }
    return { start: s, end: e };
}
